import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Gift, 
  Stamp, 
  Coffee, 
  TrendUp, 
  X,
  ArrowRight,
  Sparkle
} from "@phosphor-icons/react";
import { BRAND } from "@/styles/brand";

// ═══════════════════════════════════════════════════════════════════════════
// ONBOARDING TUTORIAL - Brand Colors
// ═══════════════════════════════════════════════════════════════════════════

const ONBOARDING_STEPS = [
  {
    id: "welcome",
    title: "Willkommen bei Franky's!",
    description: "Entdecke exklusive Vorteile als unser Gast. Lass uns dir kurz zeigen, was dich erwartet.",
    icon: Sparkle,
    color: BRAND.colors.green,
  },
  {
    id: "coupons",
    title: "Coupons & Deals",
    description: "Hier findest du exklusive Rabatte und Angebote. Zeige sie einfach an der Kasse vor und spare bei deinem Einkauf.",
    icon: Gift,
    color: BRAND.colors.green,
    tileIndex: 0,
  },
  {
    id: "stamps",
    title: "Stempelkarte",
    description: "Sammle bei jedem Besuch einen Stempel. Nach 10 Stempeln erhältst du ein Gratis-Getränk deiner Wahl.",
    icon: Stamp,
    color: BRAND.colors.orange,
    tileIndex: 1,
  },
  {
    id: "order",
    title: "Bestellen",
    description: "Bestelle dein Lieblingsgetränk vor und hole es ohne Wartezeit ab. Perfekt für den schnellen Kaffee zwischendurch.",
    icon: Coffee,
    color: BRAND.colors.tan,
    tileIndex: 2,
    comingSoon: true,
  },
  {
    id: "invest",
    title: "Partner & Invest",
    description: "Werde Teil der Franky's Familie. Investiere in neue Standorte und profitiere vom Wachstum mit uns.",
    icon: TrendUp,
    color: BRAND.colors.greenLight,
    tileIndex: 3,
    comingSoon: true,
  },
];

export default function OnboardingTutorial({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = ONBOARDING_STEPS[currentStep];
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const Icon = step.icon;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setIsVisible(false);
    localStorage.setItem("frankysfarm_onboarding_done", "true");
    setTimeout(() => onComplete(), 300);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end justify-center"
        style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      >
        {/* Backdrop click to skip */}
        <div 
          className="absolute inset-0" 
          onClick={handleSkip}
        />
        
        {/* Card */}
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
          className="relative w-full max-w-md mx-4 mb-8 rounded-3xl overflow-hidden"
          style={{ 
            backgroundColor: "#FFFFFF",
            boxShadow: "0 -10px 40px rgba(0,0,0,0.2)",
          }}
        >
          {/* Close Button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-10"
            style={{ backgroundColor: BRAND.colors.bg }}
          >
            <X size={18} style={{ color: BRAND.colors.textMuted }} />
          </button>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 pt-6 pb-2">
            {ONBOARDING_STEPS.map((_, index) => (
              <motion.div
                key={index}
                className="h-1.5 rounded-full"
                style={{ 
                  backgroundColor: index === currentStep ? step.color : "#E6DED3",
                  width: index === currentStep ? 24 : 8,
                }}
                animate={{ width: index === currentStep ? 24 : 8 }}
                transition={{ duration: 0.3 }}
              />
            ))}
          </div>

          {/* Content */}
          <div className="px-6 pt-4 pb-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.1 }}
                  className="w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5"
                  style={{ 
                    backgroundColor: `${step.color}15`,
                  }}
                >
                  <Icon size={40} weight="duotone" style={{ color: step.color }} />
                </motion.div>

                {/* Title */}
                <h2 
                  className="text-2xl font-bold mb-3"
                  style={{ 
                    color: "#1A1A1A",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  {step.title}
                  {step.comingSoon && (
                    <span 
                      className="ml-2 text-xs font-normal px-2 py-1 rounded-full"
                      style={{ 
                        backgroundColor: "#F5E6E0",
                        color: "#6B5B4F",
                      }}
                    >
                      Bald
                    </span>
                  )}
                </h2>

                {/* Description */}
                <p 
                  className="text-base leading-relaxed"
                  style={{ 
                    color: "#6B5B4F",
                    fontFamily: "'Figtree', sans-serif",
                  }}
                >
                  {step.description}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex gap-3">
            {currentStep > 0 && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex-1 py-4 rounded-2xl font-semibold text-base"
                style={{ 
                  backgroundColor: "#F5E6E0",
                  color: "#6B5B4F",
                  fontFamily: "'Figtree', sans-serif",
                }}
              >
                Zurück
              </motion.button>
            )}
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleNext}
              className="flex-1 py-4 rounded-2xl font-semibold text-base flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: step.color,
                color: "#fff",
                fontFamily: "'Figtree', sans-serif",
              }}
            >
              {isLastStep ? "Los geht's!" : "Weiter"}
              <ArrowRight size={18} weight="bold" />
            </motion.button>
          </div>

          {/* Skip Link */}
          {!isLastStep && (
            <div className="pb-6 text-center">
              <button
                onClick={handleSkip}
                className="text-sm underline"
                style={{ 
                  color: "#6B5B4F",
                  fontFamily: "'Figtree', sans-serif",
                }}
              >
                Überspringen
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
